import { findOrDie, isNotNull, mustNeverExist, notNullOrDie, reformatDate, BigNumber, indexOrDie } from "@repo/utils";
import type { AssistantDecisions, AssistantFieldType, AssistantFieldValue } from "../assistant/types.js";
import type {
    PaymentTerms,
    PurchaseOrderExtraCalculationClass,
    PurchaseOrderExtraDefinition,
    PurchaseOrderExtraType,
} from "../erp/types.js";
import type { Language } from "../organization/types.js";
import type { OrderConfirmationProcessingResult } from "../assistant/workflows/po/types.js";
import { calculateNetUnitPrice } from "../erp/calculations.js";

export { InboxIdSchema } from "../inbox/z.js";
export type { Language } from "../organization/types.js";

export { AssistantDecisionsSchema } from "../assistant/z.js";

export { adjustPurchaseOrderComparison } from "../assistant/helpers/po/comparison/adjustPurchaseOrderComparison.js";
export { PurchaseOrderComparisonSchema } from "../assistant/helpers/po/comparison/z.js";
export type { ScalarComparison, ExtraLine } from "../assistant/helpers/po/comparison/types.js";
export { evaluatePOComparisonApproval } from "../assistant/helpers/po/evaluateApprovalThresholds.js";
export { calculateConfirmedSchedules } from "../assistant/helpers/po/calculateConfirmedSchedules.js";
export { hasDeviation } from "../assistant/helpers/po/comparison/helpers.js";
export {
    deliveryStatusArbitraryKey,
    nextDeliveryStatusArbitraryKey,
    scheduleLinesAutoAccept,
} from "../assistant/po/fields/aswoDeliveryState.js";
export { calculatePositionSplits } from "../erp/sap/patch/calculatePositionSplits.js";
export {
    makeScheduleLinesAsOrdered,
    makeScheduleLinesAsOrderedDecision,
} from "../assistant/helpers/po/makeScheduleLinesAsOrdered.js";

export { getNodeSchemaByValuePath, buildDefaultValueBy } from "../assistant/universal/helpers/schema.js";

export function buildOcReclamationMessageParams(
    organizationName: string,
    decisions: AssistantDecisions,
    result: OrderConfirmationProcessingResult,
    paymentTermsLookup: Record<string, PaymentTerms>,
) {
    function getDecisionValue<T extends AssistantFieldType>(key: string) {
        const decision = decisions[key];

        if (decision === undefined || decision.type === "flagged" || decision.type === "ignored") {
            return null;
        }

        if (decision.type === "rejected") {
            return decision.includeInEmail !== false ? (decision.value as AssistantFieldValue<T>) : null;
        }

        if (decision.approval.state === "declined") {
            return decision.approval.includeInEmail !== false ? (decision.value as AssistantFieldValue<T>) : null;
        }

        return null;
    }

    const declinedDeliveryAddress = getDecisionValue<"string">("deliveryAddress")?.value;
    const declinedIncoterms1 = getDecisionValue<"deliveryTerms">("deliveryTerms")?.value.incoterms1;
    const declinedPaymentTermsId = getDecisionValue<"registry">("paymentTerms")?.value.id;

    const orderPaymentTerms = result.head.paymentTerms.orderId
        ? paymentTermsLookup[result.head.paymentTerms.orderId]
        : null;
    const declinedPaymentTerms = declinedPaymentTermsId ? paymentTermsLookup[declinedPaymentTermsId] : null;

    const item = findOrDie(result.items, x => x.type === "item_confirmed" || x.type === "missing_item");

    return {
        organizationName,
        poNumber: result.poNumber,
        poDate: result.poDate,
        ocReference: result.ocNumber,
        ocDate: result.ocDate,
        incoterms: declinedIncoterms1
            ? {
                  from: result.head.deliveryTerms.incoterms1.orderValue,
                  to: declinedIncoterms1,
              }
            : null,
        paymentTerms: declinedPaymentTermsId
            ? {
                  from: orderPaymentTerms?.textDe ?? orderPaymentTerms?.textEn ?? null,
                  to: declinedPaymentTerms?.textDe ?? declinedPaymentTerms?.textEn ?? null,
              }
            : null,
        deliveryAddress: declinedDeliveryAddress
            ? {
                  from: item.type === "item_confirmed" ? item.deliveryAddress.orderValue : item.deliveryAddress,
                  to: declinedDeliveryAddress,
              }
            : null,
        currency: result.currency,
        items: result.items.reduce(
            (acc, item, i) => {
                if (item.type !== "item_confirmed") {
                    return acc;
                }

                const declinedPrice = getDecisionValue<"price">(`items.${i}.price`);
                const declinedTotalQuantity = getDecisionValue<"quantity">(`items.${i}.totalQuantity`);
                const declinedScheduleLines = getDecisionValue<"scheduleLines">(`items.${i}.scheduleLines`);

                if (!declinedPrice && !declinedTotalQuantity && !declinedScheduleLines) {
                    return acc;
                }

                const existingConfirmations = new Set(
                    item.confirmations.map(x => `${x.reference ?? ""}_${x.quantity ?? ""}_${x.deliveryDate ?? ""}`),
                );

                const confirmations = (declinedScheduleLines?.value.confirmations ?? [])
                    .filter(x => {
                        return !existingConfirmations.has(
                            `${x.reference ?? ""}_${x.quantity.amount?.toNumber() ?? ""}_${x.deliveryDate ?? ""}`,
                        );
                    })
                    .map(x => {
                        return {
                            quantity: x.quantity.amount?.toNumber() ?? 0,
                            deliveryDate: notNullOrDie(x.deliveryDate),
                        };
                    });

                acc.push({
                    number: item.poPositionNumber,
                    description: item.description,
                    material: item.materialId,
                    uom: item.costStructure.orderQuantityUom ?? "",
                    price: declinedPrice
                        ? {
                              from: calculateNetUnitPrice(
                                  item.costStructure.orderUnitPrice ?? 0,
                                  item.costStructure.orderQuantity ?? 0,
                                  item.costStructure.orderPriceUnit ?? 1,
                                  item.costStructure.orderUomConversionCoefficient,
                                  item.poExtra,
                                  2,
                              ).toNumber(),

                              to: calculateNetUnitPrice(
                                  declinedPrice.value.amount?.amount ?? BigNumber(0),
                                  item.costStructure.orderQuantity ?? 0,
                                  item.costStructure.orderPriceUnit ?? 1,
                                  item.costStructure.orderUomConversionCoefficient,
                                  declinedPrice.value.extra,
                                  2,
                              ).toNumber(),
                          }
                        : null,
                    quantity: declinedTotalQuantity
                        ? {
                              from: notNullOrDie(item.costStructure.orderQuantity),
                              to: declinedTotalQuantity.value.amount?.toNumber() ?? 0,
                          }
                        : null,
                    confirmations,
                });
                return acc;
            },
            [] as OcReclamationMessageParams["items"],
        ),
    } satisfies OcReclamationMessageParams;
}

type Diff<T> = {
    from: T | null;
    to: T | null;
    rejectedBy?: {
        salutation: string;
        lastName: string;
    };
};
export type OcReclamationMessageParams = {
    organizationName: string;
    ocReference: string;
    ocDate?: string;
    poNumber: string;
    poDate: string;
    paymentTerms: Diff<string> | null;
    incoterms: Diff<string> | null;
    currency: string;
    deliveryAddress: Diff<string> | null;
    items: Array<{
        number: string;
        material: string | null;
        description: string | null;
        uom: string;
        price: Diff<number> | null;
        quantity: Diff<number> | null;
        confirmations: Array<{
            quantity: number;
            deliveryDate: string;
        }>;
    }>;
};

export function formatOcReclamationSubject(number: string, languages: Array<Language>) {
    return languages
        .map(x => {
            switch (x) {
                case "EN":
                    return `Rejection of OC for our PO ${number}`;
                case "DE":
                    return `Ablehnung der AB für unsere Bestellung ${number}`;
                default:
                    throw mustNeverExist(x);
            }
        })
        .join(" / ");
}

export function formatOcReclamationMessage(params: OcReclamationMessageParams, languages: Array<Language>) {
    return languages
        .map(x => {
            switch (x) {
                case "EN":
                    return formatEnglishMessage(params);
                case "DE":
                    return formatGermanMessage(params);
                default:
                    throw mustNeverExist(x);
            }
        })
        .join("\n<p><br/>---<br/><br/></p>\n");
}

function formatEnglishMessage(params: OcReclamationMessageParams) {
    return `
<p>Dear Partner,</p>
<p>Thank you for your order confirmation${params.ocReference !== "" ? ` ${params.ocReference}` : ""}${
        params.ocDate ? `, dated ${date(params.ocDate)}` : ""
    }, regarding our purchase order ${params.poNumber} dated ${date(params.poDate)}.<br/>
The following proposed changes <b>cannot</b> be accepted:</p>
<ul>
${params.deliveryAddress ? `<li>Delivery address from ${address(params.deliveryAddress.from)} to ${address(params.deliveryAddress.to)}</li>` : ""}
${params.paymentTerms ? `<li>Payment terms from ${params.paymentTerms.from} to ${params.paymentTerms.to}</li>` : ""}
${params.incoterms ? `<li>Incoterms from ${params.incoterms.from} to ${params.incoterms.to}</li>` : ""}
${params.items
    .map(item => {
        return `
<li>Our PO item no. ${item.number} (our material ${[item.material, item.description].filter(isNotNull).join(" - ")})</li>
<ul>
${item.price ? `<li>Item price from ${item.price.from?.toFixed(2)} ${params.currency} to ${item.price.to?.toFixed(2)} ${params.currency}</li>` : ""}
${item.quantity ? `<li>Quantity from ${item.quantity.from} ${item.uom} to ${item.quantity.to} ${item.uom}</li>` : ""}
${item.confirmations
    .map(
        confirmation =>
            `<li>Change in delivery date for ${confirmation.quantity} ${item.uom} to ${date(confirmation.deliveryDate)}</li>`,
    )
    .join("\n")}
</ul>`.trim();
    })
    .join("\n")}
</ul>

<p>Please update and resend your order confirmation at your earliest convenience.<br/>
If you have any questions or need further clarification, feel free to contact the team member responsible for this purchase order.</p>

<p>Kind regards,<br/>
Your ${params.organizationName} team</p>
    `.trim();
}

function formatGermanMessage(params: OcReclamationMessageParams) {
    return `
<p>Sehr geehrte/r Partner/in,</p>

<p>vielen Dank für Ihre Auftragsbestätigung ${params.ocReference}${
        params.ocDate ? ` vom ${date(params.ocDate)}` : ""
    }, die wir zu unserer Bestellung ${params.poNumber} vom ${date(params.poDate)} erhalten haben.<br/>
Leider können wir die folgenden Änderungen <b>nicht</b> akzeptieren:</p>
<ul>
${params.deliveryAddress ? `<li>Lieferadresse von ${address(params.deliveryAddress.from)} zu ${address(params.deliveryAddress.to)}</li>` : ""}
${
    params.paymentTerms
        ? `<li>Zahlungsbedingungen von ${params.paymentTerms.from} zu ${params.paymentTerms.to}</li>`
        : ""
}
${params.incoterms ? `<li>Lieferbedingungen von ${params.incoterms.from} zu ${params.incoterms.to}</li>` : ""}
${params.items
    .map(item => {
        return `
<li>Unsere Position Nr. ${item.number} (unser Material ${[item.material, item.description]
            .filter(isNotNull)
            .join(" - ")})</li>
<ul>
${item.price ? `<li>Einzelpreis von ${item.price.from?.toFixed(2)} ${params.currency} auf ${item.price.to?.toFixed(2)} ${params.currency}</li>` : ""}
${item.quantity ? `<li>Menge von ${item.quantity.from} ${item.uom} auf ${item.quantity.to} ${item.uom}</li>` : ""}
${item.confirmations
    .map(
        confirmation =>
            `<li>Änderung des Lieferdatums für ${confirmation.quantity} ${item.uom} auf ${date(confirmation.deliveryDate)}</li>`,
    )
    .join("\n")}
</ul>`.trim();
    })
    .join("\n")}
</ul>

<p>Bitte korrigieren Sie Ihre Auftragsbestätigung und senden Sie uns die überarbeitete Version schnellstmöglich zurück.<br/>
Sollten Sie Fragen haben oder weitere Informationen benötigen, steht Ihnen die zuständige Ansprechperson in unserem Team gerne zur Verfügung.</p>

<p>Mit freundlichen Grüßen<br/>

Ihr ${params.organizationName} Team</p>
    `.trim();
}

function date(value: string) {
    return reformatDate(value, "yyyy-MM-dd", "dd.MM.yyyy");
}

function address(value: string | null) {
    return value === null ? "–" : value.replaceAll("\n", "");
}

export function sendingReclamationEmailRequested(decisions: AssistantDecisions) {
    return Object.values(decisions).some(x => {
        return (
            (x.type === "rejected" && x.includeInEmail) ||
            ("approval" in x && x.approval.state === "declined" && x.approval.includeInEmail)
        );
    });
}

export function calculateNumberPrecision(reference: BigNumber | number | null, min: number) {
    return Math.max(min, BigNumber(reference ?? 0).decimalPlaces() ?? 0);
}

export function retrieveItemKeyParts(key: string) {
    const match = key.match(/^items\.(\d+)\.(\w*)/);
    if (match) {
        return {
            idx: parseInt(indexOrDie(match, 1), 10),
            datapoint: indexOrDie(match, 2),
        };
    }
    return null;
}

export function retrieveItemIndexFromKey(key: string): number | null {
    const parts = retrieveItemKeyParts(key);
    return parts !== null ? parts.idx : null;
}

export type ExtraClassTypeToName = Record<`${PurchaseOrderExtraType}_${PurchaseOrderExtraCalculationClass}`, string>;

export function buildExtraTypeClassToName(typeMap: PurchaseOrderExtraDefinition | null): ExtraClassTypeToName {
    return Object.entries(typeMap ?? {}).reduce((acc, [display, m]) => {
        acc[`${m.type}_${m.calculation.class}`] = display;
        return acc;
    }, {} as ExtraClassTypeToName);
}
